/**
 * Pages Normalizer
 * Normalizes pages API responses to match contract schema format.
 * @module api/normalizers/pages
 */

export function normalizePageItem(page) {
  if (!page || typeof page !== "object") {
    return null
  }

  // Handle legacy tree_item_* format
  if (page.tree_item_id != null) {
    return {
      id: Number(page.tree_item_id),
      name: page.tree_item_title ?? null,
      slug: page.tree_item_uri ?? null,
      active: page.tree_item_visible ?? null,
      treeItemLevel: page.tree_item_level ?? null,
    }
  }

  // Handle modern format (snake_case)
  return {
    id: page.id != null ? Number(page.id) : null,
    name: page.name ?? null,
    slug: page.slug ?? null,
    active: page.active ?? null,
    createdAt: page.created_at ?? null,
    updatedAt: page.updated_at ?? null,
  }
}

export function normalizePagesList(pages) {
  if (Array.isArray(pages)) {
    return pages.map(normalizePageItem).filter(Boolean)
  }

  if (pages && typeof pages === "object" && Array.isArray(pages.items)) {
    return pages.items.map(normalizePageItem).filter(Boolean)
  }

  return []
}

export function normalizePage(page) {
  if (!page || typeof page !== "object") {
    return null
  }

  // Handle legacy tree_item_* format
  if (page.tree_item_id != null) {
    return {
      id: Number(page.tree_item_id),
      name: page.tree_item_title ?? "",
      slug: page.tree_item_uri ?? null,
      content: page.tree_item_text ?? null,
      metaTitle: page.tree_item_window_title ?? null,
      metaDescription: page.tree_item_meta_description ?? null,
      active: page.tree_item_visible ?? null,
      treeItemLevel: page.tree_item_level ?? null,
    }
  }

  // Handle modern format (snake_case)
  return {
    id: page.id != null ? Number(page.id) : null,
    name: page.name ?? "",
    slug: page.slug ?? null,
    content: page.content ?? null,
    metaTitle: page.meta_title ?? null,
    metaDescription: page.meta_description ?? null,
    active: page.active ?? null,
    orderNum: page.order_num != null ? Number(page.order_num) : null,
    createdAt: page.created_at ?? null,
    updatedAt: page.updated_at ?? null,
  }
}

export function normalizePageField(field) {
  if (!field || typeof field !== "object") {
    return null
  }

  // Convert string IDs from PHP mysqli to numbers
  return {
    id: field.id != null ? Number(field.id) : null,
    name: field.name ?? "",
    type: field.type ?? "",
    label: field.label ?? null,
    required: field.required ?? null,
    default: field.default ?? null,
    options: field.options ?? null,
  }
}

/**
 * Normalize raw page-field payloads from either legacy array responses or the
 * current `{ fields: { key: descriptor } }` envelope.
 *
 * @precondition `fields` is either an array of raw descriptors, an object with a
 * `fields` record, or an unknown value.
 * @postcondition Returns an array of normalized page-field descriptors; unknown
 * or malformed inputs degrade to an empty array instead of throwing.
 * @sideeffects None.
 */
export function normalizePageFields(fields) {
  const rawFields = Array.isArray(fields)
    ? fields
    : fields &&
        typeof fields === "object" &&
        fields.fields &&
        typeof fields.fields === "object"
      ? Object.entries(fields.fields).map(([name, descriptor]) => ({
          name,
          ...(descriptor || {}),
        }))
      : null

  if (!Array.isArray(rawFields)) {
    return []
  }

  return rawFields.map(normalizePageField).filter(Boolean)
}

export function normalizePageDeleteResponse(response) {
  if (!response || typeof response !== "object") {
    return { result: "unknown", message: null }
  }

  return {
    result: response.result ?? "unknown",
    message: response.message ?? null,
  }
}
